import { BaseEffect } from '../BaseEffect.js';

/**
 * Phosphor glow and persistence effect implementation
 */
export class PhosphorEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            persistence: 0.7,
            glowIntensity: 0.6,
            glowRadius: 2.0,
            colorTint: 0.5,
            animate: true
        };

        super('phosphor', shaderManager, defaultParams);
    }

    /**
     * Initialize the effect
     */
    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision mediump float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_persistence;
            uniform float u_glowIntensity;
            uniform float u_glowRadius;
            uniform float u_colorTint;
            
            varying vec2 v_texcoord;
            
            #define RADIUS 3
            
            vec3 phosphorColor(vec2 uv, vec3 color) {
                vec2 pixelSize = 1.0 / u_resolution;
                vec3 glow = vec3(0.0);
                float totalWeight = 0.0;
                
                // Sample in a fixed size grid
                for(int y = -RADIUS; y <= RADIUS; y++) {
                    for(int x = -RADIUS; x <= RADIUS; x++) {
                        vec2 offset = vec2(float(x), float(y)) * pixelSize * u_glowRadius;
                        float dist = length(offset);
                        float weight = 1.0 / (1.0 + dist * 3.0);  // Reduced falloff for stronger glow
                        
                        glow += texture2D(u_image, uv + offset).rgb * weight;
                        totalWeight += weight;
                    }
                }
                glow /= totalWeight;
                
                // Add green tint to the glow
                vec3 tint = vec3(0.2, 1.0, 0.2);  // Much stronger green tint
                glow = mix(glow, glow * tint, u_colorTint);
                
                // Mix original color with glow based on intensity
                return mix(color, glow, u_glowIntensity);
            }
            
            void main() {
                vec4 color = texture2D(u_image, v_texcoord);
                
                // Apply phosphor glow effect
                vec3 glowColor = phosphorColor(v_texcoord, color.rgb);
                
                // Create multiple ghost images with different offsets
                vec3 ghostColor = vec3(0.0);
                float totalWeight = 0.0;
                
                // Create 3 ghost images with different phases
                for(int i = 1; i <= 3; i++) {
                    float phase = float(i) * 2.0 * 3.14159 / 3.0;
                    float ghostTime = u_time * 3.0 + phase;
                    
                    // Horizontal ghosting
                    vec2 ghostOffset = vec2(sin(ghostTime) * 0.005, 0.0) * u_persistence;
                    float weight = float(4 - i) / 6.0;  // Decreasing weights for each ghost
                    
                    ghostColor += texture2D(u_image, v_texcoord + ghostOffset).rgb * weight;
                    totalWeight += weight;
                }
                
                ghostColor /= totalWeight;
                
                // Mix the glowing color with the ghost images
                vec3 finalColor = mix(glowColor, ghostColor, u_persistence * 0.7);  // Increased ghost visibility
                
                gl_FragColor = vec4(finalColor, color.a);
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    /**
     * Set up effect-specific uniforms
     */
    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_persistence', { type: '1f' });
        this.addUniform('u_glowIntensity', { type: '1f' });
        this.addUniform('u_glowRadius', { type: '1f' });
        this.addUniform('u_colorTint', { type: '1f' });
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        Object.assign(uniforms, {
            u_persistence: { value: this.params.persistence, type: '1f' },
            u_glowIntensity: { value: this.params.glowIntensity, type: '1f' },
            u_glowRadius: { value: this.params.glowRadius, type: '1f' },
            u_colorTint: { value: this.params.colorTint, type: '1f' }
        });

        // For animation, we want the time to keep going
        if (!this.params.animate) {
            uniforms.u_time.value = 1.0; // Fixed position for non-animated state
        }
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            persistence: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Persistence'
            },
            glowIntensity: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Glow Intensity'
            },
            glowRadius: {
                min: 0.5,
                max: 3,
                step: 0.1,
                label: 'Glow Radius'
            },
            colorTint: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Green Tint'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 